//Zooming with the touchpad pinch or Ctrl + mouse wheel. Pure functions, no DOM, so they can
//be checked in Node (`pdf-viewer.unit.spec.ts`).

/** The range the gesture zooms in; the +/− buttons and «Seitenbreite» keep their own steps. */
export const GESTURE_MIN_SCALE = 0.5;
export const GESTURE_MAX_SCALE = 3;

/** One mouse-wheel notch arrives as ~100 px; capped so a notch zooms by ~25 %, a pinch event by a few. */
const MAX_DELTA_PX = 25;

/** `WheelEvent.deltaMode`: 0 pixels, 1 lines (Firefox mouse wheel), 2 pages. */
function deltaInPixels(deltaY: number, deltaMode: number): number {
    if (deltaMode === 1) {
        return deltaY * 16;
    }
    if (deltaMode === 2) {
        return deltaY * 400;
    }
    return deltaY;
}

/**
 * The scale after one wheel event with Ctrl (a touchpad pinch arrives the same way). Exponential, so
 * pinching in and back out returns to the same scale.
 *
 * A scale outside the gesture range (e.g. from «Seitenbreite») is not snapped into it: the range only
 * stops the gesture from going further.
 */
export function nextGestureScale(current: number, deltaY: number, deltaMode: number): number {
    const px = Math.max(-MAX_DELTA_PX, Math.min(MAX_DELTA_PX, deltaInPixels(deltaY, deltaMode)));
    const next = current * Math.exp(-px / 100);
    const min = Math.min(GESTURE_MIN_SCALE, current);
    const max = Math.max(GESTURE_MAX_SCALE, current);
    return Math.max(min, Math.min(max, next));
}

/** Where on a page the cursor is, as a share of its width and height (0..1, clamped to the page). */
export interface PageAnchor {
    page: number;
    x: number;
    y: number;
}

export interface Box {
    left: number;
    top: number;
    width: number;
    height: number;
}

/** The point under the cursor as a share of the page — the same share is the same spot at any scale. */
export function anchorOnPage(page: number, pageRect: Box, clientX: number, clientY: number): PageAnchor {
    const share = (offset: number, size: number): number => (size > 0 ? Math.max(0, Math.min(1, offset / size)) : 0);
    return {
        page,
        x: share(clientX - pageRect.left, pageRect.width),
        y: share(clientY - pageRect.top, pageRect.height),
    };
}

/**
 * The scroll position that puts the anchored spot back under the cursor.
 *
 * `page` is the page's box in the scroller's content (offsetLeft/Top and size at the new scale),
 * `cursor` the cursor relative to the scroller's visible top left. The browser clamps the result, so a
 * page narrower than the scroller simply stays centred.
 */
export function scrollToKeepAnchor(
    anchor: PageAnchor,
    page: Box,
    cursor: { x: number; y: number },
): { left: number; top: number } {
    return {
        left: page.left + anchor.x * page.width - cursor.x,
        top: page.top + anchor.y * page.height - cursor.y,
    };
}
